import { Controller } from "@hotwired/stimulus";

// Copies an artifact dora serves (the finding's /fix.patch) to the clipboard.
// The body is fetched from the URL rather than embedded in the page, so the
// clipboard holds exactly the bytes a download would give.
//
// Usage:
//   <button data-controller="copy" data-copy-url-value="/vulns/{id}/fix.patch"
//           data-action="click->copy#copy">
//     <span data-copy-target="label">copy patch</span>
//   </button>
export default class extends Controller {
  static targets = ["label"];
  static values = { url: String };

  connect() {
    this.idleLabel = this.hasLabelTarget ? this.labelTarget.textContent : "";
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer);
  }

  async copy(event) {
    const btn = event.currentTarget;
    if (this.timer) clearTimeout(this.timer);
    btn.disabled = true;
    this.setLabel("copying…");
    try {
      const res = await fetch(this.urlValue);
      if (!res.ok) throw new Error(`GET ${this.urlValue} answered ${res.status}`);
      await this.writeClipboard(await res.text());
      this.setLabel("copied ✓");
    } catch (err) {
      console.error("dora copy:", err);
      this.setLabel("copy failed");
    } finally {
      btn.disabled = false;
      this.timer = setTimeout(() => this.setLabel(this.idleLabel), 2000);
    }
  }

  setLabel(text) {
    if (this.hasLabelTarget) this.labelTarget.textContent = text;
  }

  async writeClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    // Plain-http origins get no clipboard API; the legacy path still works
    // because the call site is inside a user gesture.
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.readOnly = true;
    ta.style.cssText = "position:fixed;opacity:0";
    document.body.appendChild(ta);
    ta.select();
    try {
      if (!document.execCommand("copy")) throw new Error("execCommand copy returned false");
    } finally {
      ta.remove();
    }
  }
}
